import React from 'react'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  Link,
  BlockQuote,
  Table,
  TableToolbar,
  Alignment,
  Indent,
  HorizontalLine,
  Font,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Base64UploadAdapter,
} from 'ckeditor5'
import 'ckeditor5/ckeditor5.css'

interface RichTextEditorProps {
  /** Controlled HTML value (provided by AntD Form.Item) */
  value?: string
  /** Called with the updated HTML (consumed by AntD Form.Item) */
  onChange?: (html: string) => void
  placeholder?: string
  minHeight?: number
}

/**
 * Rich-text (WYSIWYG) editor used in the Proposal Templates master so managers
 * can type the proposal body freely with formatting. Emits HTML, which flows
 * straight into the proposal PDF generator.
 *
 * Compatible with AntD <Form.Item> — it injects `value` / `onChange`.
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  minHeight = 320,
}) => {
  return (
    <div className="rte-wrap" style={{ ['--rte-min-height' as any]: `${minHeight}px` }}>
      <style>{`
        .rte-wrap .ck-editor__editable_inline { min-height: var(--rte-min-height); }
        .rte-wrap .ck.ck-editor { width: 100%; }
      `}</style>
      <CKEditor
        editor={ClassicEditor}
        config={{
          licenseKey: 'GPL',
          placeholder,
          plugins: [
            Essentials, Paragraph, Heading, Bold, Italic, Underline, Strikethrough,
            List, Link, BlockQuote, Table, TableToolbar, Alignment, Indent,
            HorizontalLine, Font, Image, ImageCaption, ImageResize, ImageStyle, ImageToolbar, ImageUpload, Base64UploadAdapter,
          ],
          toolbar: [
            'undo', 'redo', '|',
            'heading', '|',
            'bold', 'italic', 'underline', 'strikethrough', '|',
            'fontColor', 'fontBackgroundColor', '|',
            'alignment', '|',
            'bulletedList', 'numberedList', 'outdent', 'indent', '|',
            'link', 'blockQuote', 'insertTable', 'horizontalLine', 'insertImage',
          ],
          table: {
            contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
          },
          image: {
            toolbar: [
              'imageTextAlternative',
              'toggleImageCaption',
              'imageStyle:inline',
              'imageStyle:block',
              'imageStyle:side'
            ]
          },
        }}
        data={value || ''}
        onChange={(_evt, editor) => onChange?.(editor.getData())}
      />
    </div>
  )
}

export default RichTextEditor
